import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {useMemo} from 'react';
import {Background, ReactFlow, ReactFlowProvider} from 'reactflow';

import {ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {capitalize} from 'util/NameUtil';

import buildInteractiveFlowchartFromStaticData from '../../buildInteractiveFlowchartFromStaticData';
import {StaticTemplateFlowchart} from '../../processFlowchartConfigEditorConstants';
import {
  PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES,
  PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES,
} from '../../processFlowchartConfigEditorRegistry';
import {FlowchartOptions} from '../../processFlowchartConfigEditorUtils';
import {buildTransitionIndexByPTDVPair} from '../../types/transition/transitionKeyUtils';
import {TransitionsContextProvider, TransitionsContextValue} from '../../types/transition/TransitionsContext';
import {Transition} from '../../types/transition/TransitionTypes';
import {ConfigurableTaskDefinitionGQL} from '../../useAllSchemasById';
import buildStaticFlowchartFromTransition from './buildStaticFlowchartFromTransition';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

export enum TransitionModalAction {
  EDIT = 'EDIT',
  VIEW = 'VIEW',
  ADD = 'ADD',
}

export enum TransitionType {
  PLANNED = 'PLANNED',
  OPTIONAL = 'OPTIONAL',
}

interface TransitionMode {
  action: TransitionModalAction;
  type: TransitionType;
}

export interface SingleFlowchartTransitionModalProps {
  action: TransitionModalAction;
  /** The transition to view/edit, undefined for add mode. */
  transition?: Transition;
  ptdvIdToTaskSchemaId: Record<string, string>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
}

const NOOP_FLOWCHART_OPTIONS: FlowchartOptions = {
  onFieldClick: () => {},
  onUpdateNodeData: () => Promise.resolve(null),
};

const SOURCE_NODE_X = 0;
const TARGET_NODE_X = 600;
const NODE_Y_SPACING = 250;

// TODO: This is a temporary solution to position the nodes in the transition modal
// Will be replaced when we add the shared flowchart component.
function buildNodePositions(
  staticFlowchart: StaticTemplateFlowchart
): Record<string, {x: number; y: number}> {
  const {nodesStaticData} = staticFlowchart;
  const positions: Record<string, {x: number; y: number}> = {};

  const sourceNodes = nodesStaticData.slice(0, -1);
  const targetNode = nodesStaticData[nodesStaticData.length - 1];

  const sourceStartY = -((sourceNodes.length - 1) * NODE_Y_SPACING) / 2;
  for (let i = 0; i < sourceNodes.length; i++) {
    positions[sourceNodes[i].id] = {x: SOURCE_NODE_X, y: sourceStartY + i * NODE_Y_SPACING};
  }

  if (targetNode != null) {
    positions[targetNode.id] = {x: TARGET_NODE_X, y: 0};
  }

  return positions;
}

function getModalTitle(mode: TransitionMode): string {
  const {action, type} = mode;
  const title = capitalize(action);
  return `${title} ${type.toLowerCase()} transition`;
}

function getInfoMessage(mode: TransitionMode): string | null {
  const {action, type} = mode;
  if (action === TransitionModalAction.EDIT && type === TransitionType.PLANNED) {
    return 'Saving edits to planned transitions updates the main flowchart.';
  }
  return null;
}

interface TransitionModalContentProps {
  transition?: Transition;
  ptdvIdToTaskSchemaId: Record<string, string>;
  taskDefinitionsForSidepanel: Array<ConfigurableTaskDefinitionGQL>;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
}

function TransitionModalContent(props: TransitionModalContentProps): JSX.Element {
  const {transition, ptdvIdToTaskSchemaId, taskDefinitionsForSidepanel, rootTaskSchema} = props;

  const {nodes, edges} = useMemo(() => {
    if (transition == null) {
      return {nodes: [], edges: []};
    }

    const staticFlowchart = buildStaticFlowchartFromTransition({
      transition,
      ptdvIdToTaskSchemaId,
      taskDefinitionsForSidepanel,
      rootTaskSchema,
    });

    // TODO: This is a temporary solution to build the interactive flowchart
    // Will be replaced when we add the shared flowchart component.
    return buildInteractiveFlowchartFromStaticData({
      staticNodesData: staticFlowchart.nodesStaticData,
      staticEdgesData: staticFlowchart.edgesStaticData,
      options: NOOP_FLOWCHART_OPTIONS,
      nodePositionsByNodeId: buildNodePositions(staticFlowchart),
      requireRootNode: false,
    });
  }, [transition, ptdvIdToTaskSchemaId, taskDefinitionsForSidepanel, rootTaskSchema]);

  const transitionsContextValue: TransitionsContextValue = useMemo(() => {
    const transitionsById: Record<string, Transition> =
      transition != null ? {[transition.id]: transition} : {};
    return {
      transitionsById,
      ptdvPairToTransitionIdIndex: buildTransitionIndexByPTDVPair(transitionsById),
    };
  }, [transition]);

  return (
    <TransitionsContextProvider value={transitionsContextValue}>
      <div className={css(styles.contentContainer)}>
        <div className={css(styles.canvasContainer)}>
          {/* TODO: Replace with shared flowchart editor component */}
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_NODE_TYPES}
            edgeTypes={PROCESS_FLOWCHART_CONFIG_EDITOR_EDGE_TYPES}
            fitView={true}
            proOptions={{hideAttribution: true}}
          >
            <Background gap={16} />
          </ReactFlow>
        </div>
      </div>
    </TransitionsContextProvider>
  );
}

function TransitionModalFooterInfo(props: {mode: TransitionMode}): JSX.Element | null {
  const infoMessage = getInfoMessage(props.mode);
  if (!infoMessage) {
    return null;
  }

  return (
    <div className={css(styles.footerInfo)} role="status">
      <BenchlingIcon icon="info-circle" className={css(styles.infoIcon)} />
      <span className={css(styles.infoText)}>{infoMessage}</span>
    </div>
  );
}

/**
 * A modal for editing or viewing a single flowchart transition.
 *
 * Supports default & optional transitions, and merging & single node transitions.
 */
export default function SingleFlowchartTransitionModal(
  props: SingleFlowchartTransitionModalProps
): JSX.Element {
  const {
    action,
    transition,
    ptdvIdToTaskSchemaId,
    taskDefinitionsForSidepanel,
    rootTaskSchema,
    onRequestClose,
    onRequestSubmit,
  } = props;
  const type = transition?.isPlanned === true ? TransitionType.PLANNED : TransitionType.OPTIONAL;
  const mode: TransitionMode = {action, type};
  const isViewMode = action === TransitionModalAction.VIEW;
  const buttons = isViewMode
    ? [
        {
          text: 'Close',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: onRequestClose,
        },
      ]
    : [
        {
          text: 'Cancel',
          style: ButtonStyle.TERTIARY_GRAY,
          onClick: onRequestClose,
        },
        {
          text: 'Save',
          style: ButtonStyle.PRIMARY,
          submit: true,
        },
      ];

  return (
    <Modal
      title={getModalTitle(mode)}
      extraLarge={true}
      fullHeight={true}
      bodyPadded={false}
      flexContent={true}
      onRequestClose={onRequestClose}
      onRequestSubmit={isViewMode ? undefined : onRequestSubmit}
      footer={<TransitionModalFooterInfo mode={{action, type}} />}
      buttons={buttons}
      dataTestAttrs={{component: 'TransitionModal', element: 'root'}}
    >
      <ReactFlowProvider>
        <TransitionModalContent
          transition={transition}
          ptdvIdToTaskSchemaId={ptdvIdToTaskSchemaId}
          taskDefinitionsForSidepanel={taskDefinitionsForSidepanel}
          rootTaskSchema={rootTaskSchema}
        />
      </ReactFlowProvider>
    </Modal>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    display: 'flex',
    flex: 1,
    height: '100%',
    minHeight: 0,
  },
  canvasContainer: {
    flex: 1,
    backgroundColor: Color.GRAY_50,
    position: 'relative',
  },
  footerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '36px',
  },
  infoIcon: {
    color: Color.GRAY_600,
    fontSize: '14px',
  },
  infoText: {
    fontSize: '14px',
    color: Color.GRAY_900,
  },
});
